import { useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, DelButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import { getShipingZoneList } from "services/data.internal.service";
import { deactivateShippingZone } from "services/data.internal.service";
import BasicTabs from "ui-components/BasicTabs";
import { getShippingZoneStatusCount } from "services/data.internal.service";
import { TableCard } from "ui-components/Layout";
import { ActiveButton } from "ui-components/IconButton";
import { activeShippingZone } from "services/data.internal.service";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";

function ShippingZoneList() {
  const navigate = useNavigate();
  const {permissions, allowAll} = useTeamValue();
  const {customerCode} = useCustomerValue()
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);

  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([{ label: "Active", count: 0 },
  { label: "Inactive", count: 0 }]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries("shipping-zone-list");
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const addPermission = allowAll || (Boolean(permissions["Shipping Zone"]) && permissions["Shipping Zone"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["Shipping Zone"]) && permissions["Shipping Zone"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["Shipping Zone"]) && permissions["Shipping Zone"].findIndex(b => b==="deactivate") !== -1);
  
  useQuery({
    queryKey: ["shipping-zone-list", { customerCode,active }],
    queryFn: () => getShipingZoneList({active}),
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["shipping-zone-list", "status",{customerCode}],
    queryFn: () => getShippingZoneStatusCount(),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivateShippingZone, { onSuccess, onError });
  const activeMutation = useMutation(activeShippingZone, { onSuccess, onError });
  const onEdit = (req) => navigate("/setup/shipping-zone/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.shippingZone}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.shippingZone}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Shipping Zone", accessorKey: "shippingZone" },
    { header: "Postcode From", accessorKey: "postcodeFrom" },
    { header: "Postcode To", accessorKey: "postcodeTo" },
    { header: "Ship Zone Group 1", accessorKey: "shipZoneGroup1" },
    { header: "Country Code", accessorKey: "countryCode" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { shippingZone } = row.original;
        if (active) {
          return (
            <>
              <EditButton title="edit shipping zone" disabled={!editPermission} onClick={() => onEdit({ shippingZone })} />
              <DelButton
                title="deactivate shipping zone"
                disabled={!deactivatePermission}
                onClick={() => onDelete({ shippingZone })}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate shipping zone"
              disabled={!editPermission}
              onClick={() => onActive({ shippingZone })}
            />
          );
        }
      },
    },
  ];
  const rows = data.map((company) => {
    const { shippingZone, postcodeFrom, countryCode, postcodeTo, shipZoneGroup1 } = company;
    return {
      shippingZone,
      postcodeFrom,
      countryCode,
      postcodeTo,
      shipZoneGroup1,
    };
  });
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" margin="10px">
          <MDButton
            variant="gradient"
            color="info"
            disabled={!addPermission}
            onClick={() => navigate("/setup/shipping-zone/add")}
          >
            <Icon>add</Icon>&nbsp;shipping zone
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={rows} />
      </TableCard>
    </ListLayout>
  );
}

export default ShippingZoneList;
